import { Chord, Tag, UltimateGuitarParser, type Line, type Song } from 'chordsheetjs';
import { preNormalize } from '../chart/preNormalize.ts';

// Widok pelnego tekstu (przelaczalny), specyfikacja
// "widok-tekstu-i-polish.md" sekcja 1. Ten modul jest CELOWO prostszy niz
// `chart/compile.ts`: nie scala sekcji, nie degraduje, nie liczy sygnatur -
// tylko odtwarza caly tekst z akordami tak, jak siedzi w `rawTab`, do
// przewijania. Dlatego parsuje `rawTab` SAM, wlasnym wywolaniem
// `UltimateGuitarParser`, zamiast dzielic `Chart` z reszty potoku.

/** Transponuje jeden token akordu o `delta` poltonow. Token, ktorego
 * `Chord.parse` nie rozpozna (smiec w zrodle, "N.C." i podobne), wraca BEZ
 * zmian - to samo zachowanie co `transponujToken` w `transpozycja.ts`, tu
 * bez wyboru pisowni (krzyzyk/bemol), bo specyfikacja etapu nie tego zada. */
function transponujToken(token: string, delta: number): string {
  if (delta === 0) return token;
  const akord = Chord.parse(token.trim());
  if (akord === null) return token;
  return akord.transpose(delta).toString();
}

/** Zwraca `true`, gdy `item` jest ChordLyricsPair (ma pole `chords` typu
 * string) - odrzuca `Tag`/`Comment` osadzone w tej samej linii. */
function jestParaAkordTekst(item: unknown): item is { chords: string; lyrics: string | null } {
  return typeof item === 'object' && item !== null && 'chords' in item && typeof (item as { chords: unknown }).chords === 'string';
}

/** Etykieta naglowka sekcji, np. "Verse 1", "Chorus" - z atrybutu `label`
 * tagu, z fallbackiem na jego `value` (patrz `Tag.label` w chordsheetjs). */
function etykietaSekcji(line: Line): string {
  const tag = (line as unknown as { _tag: Tag | null })._tag;
  return tag?.label || line.type || '';
}

/** Kanoniczne nazwy sekcji (po `preNormalize` zawsze angielskie) w wersji
 * dla śpiewającego. Nazwa spoza słownika zostaje taka, jak przyszła. */
const PO_POLSKU: ReadonlyArray<[RegExp, string]> = [
  [/^pre-?chorus\b/i, 'Przedrefren'],
  [/^chorus\b/i, 'Refren'],
  [/^verse\b/i, 'Zwrotka'],
  [/^bridge\b/i, 'Most'],
  [/^intro\b/i, 'Wstęp'],
  [/^outro\b/i, 'Zakończenie'],
  [/^instrumental\b/i, 'Instrumentalnie'],
  [/^interlude\b/i, 'Przerywnik'],
  [/^solo\b/i, 'Solo'],
];

export function nazwaSekcjiPoPolsku(etykieta: string): string {
  const e = etykieta.trim();
  for (const [wzor, polska] of PO_POLSKU) {
    if (wzor.test(e)) return (polska + e.replace(wzor, '')).replace(/\s+/g, ' ').trim();
  }
  return e;
}

/** Buduje pare wierszy (akordy, tekst) wyrownanych znak w znak, tak zeby
 * kazdy akord siedzial nad poczatkiem swojego fragmentu tekstu. Gdy nazwa
 * akordu po transpozycji jest dluzsza niz miejsce zajete dotad przez tekst,
 * wiersz tekstu dostaje dodatkowa spacje - to standardowa technika ukladania
 * "akordy nad slowami", nie usterka. */
function zbudujParaWierszy(line: Line, przesuniecie: number): { chords: string; tekst: string } {
  let chordLine = '';
  let txtLine = '';
  for (const item of line.items) {
    if (!jestParaAkordTekst(item)) continue;
    const lyrics = item.lyrics ?? '';
    const chordRaw = item.chords.trim();
    const chord = chordRaw ? transponujToken(chordRaw, przesuniecie) : '';

    const start = Math.max(chordLine.length, txtLine.length);
    chordLine = chordLine.padEnd(start, ' ');
    txtLine = txtLine.padEnd(start, ' ');
    chordLine += chord;
    txtLine += lyrics;
  }
  return { chords: chordLine.trimEnd(), tekst: txtLine };
}

/** Renderuje `song` (po `preNormalize` + `UltimateGuitarParser`) do `container`
 * jako pelny tekst z akordami nad wersami, przewijalny. */
function renderujSong(container: HTMLElement, song: Song, przesuniecie: number): void {
  const root = document.createElement('div');
  root.className = 'pt-container';

  let sectionIndex = 0;
  let lineIndex = 0;
  for (const line of song.lines) {
    if (line.isSectionStart()) {
      const lbl = document.createElement('div');
      lbl.className = 'pt-lbl';
      lbl.dataset.sectionIndex = String(sectionIndex);
      lbl.textContent = nazwaSekcjiPoPolsku(etykietaSekcji(line));
      root.appendChild(lbl);
      sectionIndex++;
      continue;
    }
    if (line.isSectionEnd()) continue;

    if (line.isEmpty() || !line.hasRenderableItems()) {
      const pusta = document.createElement('div');
      pusta.className = 'pt-pusta';
      root.appendChild(pusta);
      lineIndex++;
      continue;
    }

    const { chords, tekst } = zbudujParaWierszy(line, przesuniecie);

    const grupa = document.createElement('div');
    grupa.className = 'pt-grupa';
    grupa.dataset.lineIndex = String(lineIndex);

    if (chords.length > 0) {
      const wierszAkordow = document.createElement('div');
      wierszAkordow.className = 'pt-ch';
      wierszAkordow.textContent = chords;
      grupa.appendChild(wierszAkordow);
    }

    const wierszTekstu = document.createElement('div');
    wierszTekstu.className = 'pt-txt';
    wierszTekstu.textContent = tekst;
    grupa.appendChild(wierszTekstu);

    root.appendChild(grupa);
    lineIndex++;
  }

  container.replaceChildren(root);
}

/**
 * Renderuje pelny tekst utworu (akordy nad wersami) do `container`, po
 * `preNormalize` i parserze `UltimateGuitarParser`, DOPASOWUJAC rozmiar
 * czcionki tak, zeby calosc zmiescila sie na ekranie bez scrollowania.
 * Bisekcja od 14px w dol do 7px. Jesli nawet 7px nie miesci sie,
 * zostaje 7px ze scrollowaniem.
 */
export function renderujPelnyTekst(container: HTMLElement, rawTab: string, przesuniecie: number): void {
  const preNorm = preNormalize(rawTab);
  let song: Song;
  try {
    song = new UltimateGuitarParser().parse(preNorm.text);
  } catch {
    const blad = document.createElement('div');
    blad.className = 'pt-container zar-blad';
    blad.textContent = 'Nie udalo sie rozpoznac tego utworu do widoku pelnego tekstu.';
    container.replaceChildren(blad);
    return;
  }
  renderujSong(container, song, przesuniecie);

  const root = container.querySelector('.pt-container') as HTMLElement | null;
  if (!root) return;

  const MAX_PX = 14;
  const MIN_PX = 7;
  let lo = MIN_PX;
  let hi = MAX_PX;
  let best = MIN_PX;

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    root.style.fontSize = `${mid}px`;
    if (container.scrollHeight <= container.clientHeight) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  root.style.fontSize = `${best}px`;
}
